import EventBus from '@/event-bus';

export default {
  /**
   * Mutation for updating the query params for filters
   * @function
   * @param {object} state - current state in the search store
   * @param {string} filterValues - the new value for the filter
   * @param {string} filterName - name of the filter to update
   * @return {undefined}
   */
  updateQueryFilters(state, { filterValues, filterName }) {
    state.query = { ...state.query, ...{ [filterName]: filterValues, page: 1 } };
  },

  /**
   * Introduced when search started to have 2 types of url, a simplified (Google friendly) and your traditional query string.
   * Sets API url that represents the search the user wants to do.
   * @param stage
   * @param url
   */
  updateSearchUrl(state, url) {
    EventBus.$emit('bus:disable-all-filters-while-search-request-is-being-processed');
    state.searchUrl = url;
  },

  /**
   * Introduced when search started to have 2 types of url, a simplified (Google friendly) and your traditional query string.
   * Pauses state.query watcher, which means when state.query changes the search request is NOT submitted
   * @param state
   */
  pauseQueryWatcher(state) {
    state.pauseQueryWatcher = true;
  },

  /**
   * Introduced when search started to have 2 types of url, a simplified (Google friendly) and your traditional query string.
   * Unpauses state.query watcher, which means when state.query changes the search request is submitted
   * @param state
   */
  unPauseQueryWatcher(state) {
    state.pauseQueryWatcher = false;
  },
  // your search-jobs, search-reviews, so no filters applied
  setDefaultUrls(state, urls) {
    state.defaultUrls = urls;
  },
  setSortingUrls(state, urls) {
    state.sortingUrls = urls;
  },
  replaceQuery(state, query) {
    state.query = query;
  },

  /**
   * Mutation to update the sort order
   * @function
   * @param {object} state - current state in the search store
   * @param {string} sortParams - the new sort order to apply
   * @return {undefined}
   */
  updateQuerySort(state, { sortParams }) {
    state.query.od = sortParams;
  },

  /**
   * Mutation to update the current page of the query params. changed by the search-pagination component
   * @function
   * @param {object} state - current state in the search store
   * @param {number} page - the new page number
   * @return {undefined}
   */
  updateQueryPage(state, { page }) {
    state.query.page = page;
  },

  /**
   * Mutation to reset the query to an empty state. Used by search-quick-filters and search-type
   * @function
   * @param {object} state - current state in the search store
   * @param {object} query - any new query parameters to override the default reset
   * @return {undefined}
   */
  resetQueryParams(state, { query = {} } = {}) {
    state.resultCount = 0;
    state.results = [];
    state.services.featuredResults = [];

    // Custom event for vanilla js mobile search input field
    EventBus.$emit('bus:search-mutations:clear');

    const reset = {
      best_apprenticeship_employers: [],
      programme: [],
      discipline: [],
      disciplines_parent: false,
      location: '',
      // locations_parent: false,
      od: '',
      page: 1,
      s: '',
      show: '',
      radius: 10,
    };

    state.query = { ...state.query, ...reset, ...query };
  },

  /**
   * Mutation that removes one query params from the search-quick-filter component
   * @function
   * @param {object} state - current state in the search store
   * @param {string|array} value - empty strings when reseting text search.
   * @param {object} queryParam - the query parameter to update
   * arrays when remove individual values.
   * 'multiple' when clearing out all values in a filter
   * @return {undefined}
   */
  removeQuickTag(state, { value, queryParam }) {
    if (queryParam === 's') {
      state.query.s = '';
      // Custom event for vanilla js search input field
      EventBus.$emit('bus:search-mutations:clear');
    } else if (queryParam === 'disciplines_parent') {
      state.query[queryParam] = false;
      const subTags = queryParam.replace('s_parent', '');
      state.query[subTags] = [];
    } else if (queryParam === 'best_apprenticeship_employers') {
      state.query[queryParam] = [];
    } else if (queryParam === 'location') {
      state.query[queryParam] = '';
    } else if (queryParam === 'programme' && value !== 'multiple') {
      state.query[queryParam] = state.query[queryParam].filter((queryValue) => {
        return queryValue !== value;
      });
    } else if (value === 'multiple') {
      // If Multiple Filters is shown we reset the array of child filters
      state.query[queryParam] = [];
      console.log(state.query);
    } else {
      state.query[queryParam] = state.query[queryParam].filter((queryValue) => {
        return queryValue !== value;
      });
    }
  },

  /**
   * Mutation to set the filters and counts shown down the left sidebar of search
   * @function
   * @param {object} state - current state in the search store
   * @param {object} aggregations - the aggregations/filters return by elasticsearch
   * @return {undefined}
   */
  setSearchFilters(state, { aggregations }) {
    state.filters.best_apprenticeship_employers = aggregations.filtered_by_teds;

    if (state.query.show === 'jobs' || state.query.show === 'reviews') {
      state.filters.jobTypes = aggregations.filtered_by_job_types;
      state.filters.jobRoles.values = aggregations.filtered_by_role;
      state.filters.jobRoles.groups = aggregations.filtered_by_parent_role;
      // state.filters.regions = aggregations.filtered_by_region;
    } else if (state.query.show === 'companies') {
      state.filters.companyIndustries = aggregations.filtered_by_company_industry;
    }
  },

  setSearchSuggestions(state, { suggestions }) {
    state.suggestions = suggestions;
  },

  /**
   * Mutation to set the pagination object used by search-pagination
   * @function
   * @param {object} state - current state in the search store
   * @param {object} pagination - pagination object containing pages to display and next/prev buttons
   * @return {undefined}
   */
  setSearchPagination(state, { pagination }) {
    state.pagination = pagination;
  },

  /**
   * Mutation to set the result count used by search-result-total
   * @function
   * @param {object} state - current state in the search store
   * @param {number} count - count of results
   * @return {undefined}
   */
  setSearchCount(state, { count }) {
    state.resultCount = count;
  },

  /**
   * Mutation to set the main results shown in the central column by search-results
   * @function
   * @param {object} state - current state in the search store
   * @param {array} results - array of job/company/review results to show in the main column
   * @return {undefined}
   */
  setSearchResults(state, { results }) {
    state.results = results;
  },

  /**
   * Mutation to set any paid services shown at the top of search-results e.g keyword search services / feature vacancies / featured reviews / rotating button adverts
   * @function
   * @param {object} state - current state in the search store
   * @param {array} services - array of results for keyword search services / feature vacancies / featured reviews & rotating button adverts
   * @return {undefined}
   */
  setSearchServices(state, { services }) {
    const {
      featured_results: featuredResults = [],
    } = services;

    state.services.featuredResults = featuredResults;
  },

  /**
   * Mutation that sets a flag that determines whether the page is being loaded from the server
   * @function
   * @param {object} state - current state in the search store
   * @param {boolean} first - value to set the flag as
   * @return {undefined}
   */
  setFirstLoading(state, { first }) {
    state.isFirstLoading = first;
  },
  /**
    * Mutation to set the page dynamic headers
    * @function
    * @param {object} dynamicHeaders - contains the properties heading & description for the current page
    * @return {undefined}
  */
  setDynamicHeaders(state, { dynamicHeaders }) {
    // NOTE: set the vanilla search headers here
    // Vanilla search headers must be set here - Vue component stripped away in favour of blade to deliver search headers.
    // Headers still delivered via ajax in the store
    if (document.querySelector('.VanillaSearch-title')) {
      document.querySelector('.VanillaSearch-title').textContent = dynamicHeaders.heading;
    }

    if (document.querySelector('.VanillaSearch-description')) {
      document.querySelector('.VanillaSearch-description').textContent = dynamicHeaders.description;
    }
    state.dynamicHeaders = dynamicHeaders;
  },

  /**
   * Mutation to set the base url used for ajax requests
   * @function
   * @param {object} state - current state in the search store
   * @param {string} baseUrl - path for the base url
   * @return {undefined}
   */
  setBaseUrl(state, { baseUrl }) {
    state.config.baseUrl = baseUrl;
  },

  /**
   * Mutation to set the loading state which the search-loading component uses to show a loading spinner
   * @function
   * @param {object} state - current state in the search store
   * @param {boolean} loadingState - toggles the loading state on/off
   * @return {undefined}
   */
  setLoadingState(state, { loadingState }) {
    state.uiLoadingState = loadingState;
  },

  /**
   * Mutation to add a new cancellation token for axios to the store. Cancellation tokens are used to cancel pending ajax requests
   * @function
   * @param {object} state - current state in the search store
   * @param {function} cancellationToken - the function to call to cancel a specific token
   * @return {undefined}
   */
  addCancellationToken(state, { cancellationToken }) {
    state.ajaxCancellationTokens.push(cancellationToken);
  },

  /**
   * Mutation to clear out cancellation tokens. Once an ajax request completes the tokens are cleared
   * @function
   * @param {object} state - current state in the search store
   * @return {undefined}
   */
  resetCancellationTokens(state) {
    state.ajaxCancellationTokens = [];
  },

  /**
   * Mutation to set the intial query state used by the filters down the left
   * @function
   * @param {object} state - current state in the search store
   * @param {object} queryParams
   * @param {string} defaultSort
   * @return {undefined}
   */
  setInitialQueryState(state, { queryParams, defaultSort }) {
    const newState = { ...state.query, ...queryParams };

    if (!queryParams.od) {
      newState.od = defaultSort;
    }

    state.query = newState;
  },

  /**
   * Mutation to set whether the route navigation was triggered by a browser back/forward button or a UI click.
   * Used to tweak the behaviour of the scroll top
   * @function
   * @param {object} state - current state in the search store
   * @param {boolean} toggle - new boolean value for the property
   * @return {undefined}
   */
  setTriggeredByBrowser(state, { toggle }) {
    state.triggeredByBrowserUI = toggle;
  },

  setDefaultSortOrders(state, { defaultSorts }) {
    state.defaultSortOrders = defaultSorts;
  },

  setIgnoreAggregations(state, { ignoreAggregations }) {
    state.ignoreAggregations = ignoreAggregations;
  },

  /**
   * Mutation to set the locations_meta object used by search-alternative-locations
   * @function
   * @param {object} state - current state in the search store
   * @param {object} locationsMeta - locationsMeta object
   * @return {undefined}
   */
  setSearchLocationsMeta(state, { locationsMeta }) {
    state.locationsMeta = locationsMeta;
  },
};
